import { computed, readonly, ref } from 'vue'

export type Route =
  | { name: 'home' }
  | { name: 'login' }
  | { name: 'new' }
  | { name: 'editor'; id: string }
  | { name: 'public'; slug: string }
  | { name: 'show'; slug: string }
  | { name: 'remote'; slug?: string }
  | { name: 'admin' }
  | { name: 'not-found' }

const currentPath = ref(`${window.location.pathname}${window.location.search}`)

function cleanPart(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export function parseRoute(pathname = window.location.pathname): Route {
  const parts = pathname.split('/').filter(Boolean).map(cleanPart)
  if (parts.length === 0) return { name: 'home' }
  if (parts.length === 1 && parts[0] === 'login') return { name: 'login' }
  if (parts.length === 1 && parts[0] === 'new') return { name: 'new' }
  if (parts.length === 1 && parts[0] === 'admin') return { name: 'admin' }
  if (parts.length === 1 && parts[0] === 'remote') return { name: 'remote' }
  if (parts.length === 2 && parts[0] === 'deck') {
    return { name: 'editor', id: parts[1]! }
  }
  if (parts.length === 2 && parts[0] === 'p') {
    return { name: 'public', slug: parts[1]! }
  }
  if (parts.length === 3 && parts[0] === 'p' && parts[2] === 'show') {
    return { name: 'show', slug: parts[1]! }
  }
  if (parts.length === 3 && parts[0] === 'p' && parts[2] === 'remote') {
    return { name: 'remote', slug: parts[1]! }
  }
  return { name: 'not-found' }
}

const route = computed(() => parseRoute(currentPath.value.split('?')[0]))

export function navigate(to: string, replace = false): void {
  if (replace) window.history.replaceState({}, '', to)
  else window.history.pushState({}, '', to)
  currentPath.value = `${window.location.pathname}${window.location.search}`
  window.scrollTo({ top: 0, behavior: 'instant' })
}

window.addEventListener('popstate', () => {
  currentPath.value = `${window.location.pathname}${window.location.search}`
})

document.addEventListener('click', (event) => {
  if (
    event.defaultPrevented
    || event.button !== 0
    || event.metaKey
    || event.ctrlKey
    || event.shiftKey
    || event.altKey
  ) return
  const target = event.target as Element | null
  const anchor = target?.closest<HTMLAnchorElement>('a[href]')
  if (
    !anchor
    || anchor.target
    || anchor.hasAttribute('download')
    || anchor.origin !== window.location.origin
    || anchor.pathname.startsWith('/api/')
    || anchor.pathname === '/sso'
  ) return
  event.preventDefault()
  navigate(`${anchor.pathname}${anchor.search}${anchor.hash}`)
})

export function useRouter() {
  return {
    route: readonly(route),
    path: readonly(currentPath),
    navigate,
  }
}
